import React, { useState } from "react";
import Table from "../../Components/Table";
import StatusHandler from "../../Components/Table/StatusHandler";
import { Confirmation, Error, Success } from "../../Components/Modal";
import { Link } from "react-router-dom";
import useTableControls from "../../Hooks/useTableControls";
import useFetchData from "../../Hooks/useFetchData";
import ModalImageViewer from "../../Components/ModalImageViewer";
import {
  getVehicleTypes,
  changeVehicleTypeStatus,
} from "../../Services/VehicleType";
import { format_date, getSerialNumber } from "../../Utils/helpers";
import PageHeader from "../../Components/PageHeader";
import { useMutation } from "react-query";
import { image_url } from "../../Utils/connection_strings";

export default function VehicleTypeManagement() {
  // const sampleData = [
  //   {
  //     sn: "1",
  //     typeName: "Abc Type",
  //     cost: "123",
  //     numberOfVehicles: "02",
  //     datePosted: "01/02/2022",
  //     status: true,
  //   },
  //   {
  //     sn: "2",
  //     typeName: "Abc Type",
  //     cost: "123",
  //     numberOfVehicles: "02",
  //     datePosted: "01/02/2022",
  //     status: false,
  //   },
  //   {
  //     sn: "3",
  //     typeName: "Abc Type",
  //     cost: "123",
  //     numberOfVehicles: "02",
  //     datePosted: "01/02/2022",
  //     status: true,
  //   },
  //   {
  //     sn: "4",
  //     typeName: "Abc Type",
  //     cost: "123",
  //     numberOfVehicles: "02",
  //     datePosted: "01/02/2022",
  //     status: true,
  //   },
  // ];

  const {
    perPage,
    setPerPage,
    status,
    setStatus,
    search_string,
    setSearchString,
    from,
    setFrom,
    to,
    setTo,
  } = useTableControls();

  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);

  const clean_image_url = image_url?.endsWith("/") ? image_url.slice(0, -1) : image_url;
  const getFullImageUrl = (raw) => {
    if (!raw) return "";
    if (raw.startsWith("http")) return raw;
    const normalized = raw.startsWith("/") ? raw.slice(1) : raw;
    return `${clean_image_url}/${normalized}`;
  };

  const openImageViewer = (rawImage) => {
    const url = getFullImageUrl(rawImage);
    if (!url) return;
    setViewerImages([url]);
    setViewerStartIndex(0);
    setShowImageViewer(true);
  };

  const {
    // INTERNAL EXPORTS
    page,
    setPage,
    // REACT QUERY EXPORTS
    isFetching,
    isLoading,
    data,
    refetch,
  } = useFetchData("vehicleType_logs", getVehicleTypes, [
    perPage,
    search_string,
    status,
    from,
    to,
  ]);
  // console.log("vehicleType_logs Data: ", data?.data);

  const { mutate, isLoading: loadingStatus } = useMutation(
    (id) => changeVehicleTypeStatus(id),
    {
      onSuccess: (res) => {
        refetch();
        Success("Vehicle Type", res.data?.message);
      },
      onError: (err) => {
        Error(err?.response?.data?.message);
      },
    }
  );

  return (
    <div className="app-content content">
      <div className="content-wrapper">
        <div className="content-body">
          {/* Basic form layout section start */}
          <section id="configuration">
            <div className="row">
              <div className="col-12">
                <div className="user-details-wrapper">
                  <PageHeader
                    title="Vehicle Type Management"
                    subtitle="Define vehicle classes, load capacities, and type-specific parameters"
                    action={
                      <Link
                        to="/vehicle-type-management/add"
                        className="siteBtn header-btn-standard w-100-mobile"
                      >
                        <i className="fas fa-plus me-2"></i> Add New Type
                      </Link>
                    }
                  />

                  <Table
                    key={"vehicle-type-management-table"}
                    tableTitles={[
                      "S.No.",
                      "Image",
                      "Type Name",
                      "Weight Limit",
                      "Cost",
                      "Helper Count",
                      "Number of Vehicles",
                      "Date Posted",
                      "Status",
                      "Action",
                    ]}
                    dateFilter
                    showEntries
                    sortBy
                    isLoading={isLoading}
                    isFetching={isFetching}
                    data={data?.data?.data?.docs}
                    totalPages={Math.ceil(data?.data?.data?.totalPages || 0)}
                    totalDocs={data?.data?.data?.totalDocs}
                    page={page}

                    setPage={setPage}
                    setSearchString={setSearchString}
                    status={status}
                    setStatus={setStatus}
                    perPage={perPage}
                    setPerPage={setPerPage}
                    from={from}
                    setFrom={setFrom}
                    to={to}
                    setTo={setTo}
                  >
                    {data?.data?.data?.docs?.map((item, index) => (
                        <tr key={item?._id || index}>
                          <td>{getSerialNumber(data?.data?.data, index)}</td>
                          <td>
                            <div className="img-thumbnail-premium shadow-sm border rounded-3" style={{ width: '100px', height: '60px', overflow: 'hidden', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {(item.vehicleImage || item.image) ? (
                                <img
                                  src={getFullImageUrl(item.vehicleImage || item.image)}
                                  alt={item.name}
                                  className="w-100 h-100"
                                  style={{ objectFit: 'contain', cursor: 'pointer' }}
                                  onClick={() => openImageViewer(item.vehicleImage || item.image)}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://placehold.co/400x300?text=Not+Found";
                                  }}
                                />
                              ) : (
                                <div className="text-muted opacity-50">
                                  <i className="fas fa-truck" style={{ fontSize: '1.5rem' }}></i>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{item.name}</td>
                          <td>{(item.weight || item.weightLimit) ? `${item.weight || item.weightLimit} lbs` : "N/A"}</td>
                          <td>${item.baseFare}</td>
                          <td>
                            <span className={`badge ${item.helperCount > 0 ? 'bg-success bg-opacity-10 text-success' : 'bg-secondary bg-opacity-10 text-secondary'}`} style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
                              <i className={`fas fa-user-friends me-1`}></i> {item.helperCount || 0}
                            </span>
                          </td>
                          <td>{item.vehicles}</td>
                          <td>
                            {item?.createdAt &&
                              format_date(item?.createdAt, "MM/DD/YYYY")}
                          </td>
                          <td>{item.active ? "Active" : "Inactive"}</td>
                          <td>
                            <StatusHandler
                              key={`${item?._id}-status`}
                              edit
                              active
                              view2
                              item={item}
                              isLoading={loadingStatus}
                              redirectUrl={`/vehicle-type-management/edit/${item?._id}`}
                              viewUrl={`/vehicle-type-management/view/${item?._id}`}
                              onClick={() =>
                                Confirmation(
                                  `Are you sure you want to ${item?.active ? "inactive" : "active"
                                  }  ${item.name}`,
                                  "Yes",
                                  () => mutate(item?._id)
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                  </Table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <ModalImageViewer
        show={showImageViewer}
        images={viewerImages}
        startIndex={viewerStartIndex}
        onHide={() => setShowImageViewer(false)}
        title="Vehicle Type Image"
      />
      <style>{`
                .fw-800 { font-weight: 800; }
                
                @media (max-width: 576px) {
                    .w-100-mobile { width: 100% !important; text-align: center; justify-content: center; }
                    .user-details-wrapper { padding: 0.5rem; }
                }
            `}</style>
    </div>
  );
}
